import {
  DATE_FORMAT_MMDDYYYY,
  DayIntervalUnit,
  FeatureName,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  RenderListItemPropsInterface,
} from 'react-native-dropdown-picker';
import { Colors } from '../../../themes/colors';
import { CheckboxOption, CheckboxState } from '../../crop-circle/Components/CheckboxOption';
import { CloseButton } from '../../crop-circle/CloseButton';
import { DeleteIcon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import {
  ControlledSelect,
  DatePickerSelector,
  useFormContext,
} from '../../forms';
import AlertLogicInputOld from './alertLogicInput-old';
import { BaseNumberInput } from './baseNumberInput';
import {
  cropCircleDropdownStyles,
  getCropCircleDropDownPickerProps,
} from './cropCircleAlertDropdown';
import {
  AlertInputs,
  AlertType,
  ClearButtonProps,
  DeleteLogicProps,
} from './types';

type AlertLogicInputProps = {
  type: AlertType;
  isPrimaryAlertInput: boolean;
  uomLabel?: string;
  setAdditionalLogic: (value: boolean) => void;
  onDropdownOpen?: () => void;
  onDropdownClose?: () => void;
};

// TODO: Crop Circle Redesign: Remove after feature is fully rolled out
export default function AlertLogicInput(props: AlertLogicInputProps) {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  if (!isCropCircleRedesignEnabled) {
    return <AlertLogicInputOld {...props} />;
  }
  return <AlertLogicInputBase {...props} />;
}

const datePickerContainerCropCircle = {
  borderRadius: 4,
  backgroundColor: tokens.colors.neutral[200],
  borderWidth: 0,
  minHeight: 52,
  padding: 0, // Override dateRangeFieldContainer's padding: 8 - text and icon have their own 16px
  paddingHorizontal: 0,
  paddingVertical: 0,
  justifyContent: 'center' as const,
  height: '100%' as const,
};

const datePickerModalCropCircle = {
  pickerContainerStyleIOS: {
    backgroundColor: tokens.colors.neutral[100],
    paddingHorizontal: tokens.spacing.lg,
  },
  modalStyleIOS: {
    backgroundColor: tokens.colors.neutral[100],
  },
  confirmButtonStyleIOS: {
    backgroundColor: tokens.colors.neutral[200],
    borderTopWidth: 0.5,
    borderTopColor: tokens.colors.neutral[200],
  },
};

function AlertLogicInputBase({
  type,
  isPrimaryAlertInput,
  uomLabel,
  setAdditionalLogic,
  onDropdownOpen,
  onDropdownClose,
}: AlertLogicInputProps) {
  const {
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  const handleDropdownOpen = () => {
    onDropdownOpen?.();
  };

  const handleDropdownClose = () => {
    onDropdownClose?.();
  };

  const AlertItem = <T,>({
    label,
    isSelected,
    onPress,
    item,
  }: RenderListItemPropsInterface<T>) => {
    const handlePress = () => {
      onPress(item as any);
    };
    const radioButtonValue: CheckboxState = isSelected ? 'checked' : 'unchecked';
    return (
      <Pressable onPress={() => handlePress()} style={cropCircleDropdownStyles.listItem}>
        <Text style={cropCircleDropdownStyles.itemLabel}>{label}</Text>
        <CheckboxOption
          mode="radio"
          state={radioButtonValue}
          onPress={() => handlePress()}
          style={{
            backgroundColor: 'transparent',
            padding: 0,
            borderRadius: 0,
            minHeight: 0,
          }}
        />
      </Pressable>
    );
  };

  const dayIntervalUnitItems = [
    {
      label: t('alerts.dateInterval.minutes'),
      value: DayIntervalUnit.Minutes,
    },
    {
      label: t('alerts.dateInterval.hours'),
      value: DayIntervalUnit.Hours,
    },
    {
      label: t('alerts.dateInterval.days'),
      value: DayIntervalUnit.Days,
    },
    {
      label: t('alerts.dateInterval.weeks'),
      value: DayIntervalUnit.Weeks,
    },
    {
      label: t('alerts.dateInterval.months'),
      value: DayIntervalUnit.Months,
    },
  ];


  const alertPlaceholder: Record<AlertType, { placeholder: string }> = {
    [AlertType.DaysSkipped]: { placeholder: t('alerts.numberOfDays') },
    [AlertType.Date]: { placeholder: DATE_FORMAT_MMDDYYYY },
    [AlertType.Interval]: { placeholder: t('alerts.setInterval') },
    [AlertType.Min]: { placeholder: t('alerts.number') },
    [AlertType.Max]: { placeholder: t('alerts.number') },
    [AlertType.RoutineCompletions]: { placeholder: t('alerts.numberOfTimes') },
    [AlertType.RecurringInterval]: { placeholder: t('alerts.setInterval') },
  };

  const ClearButton = ({ inputType }: ClearButtonProps) => {
    return (
      <CloseButton
        onPress={() => {
          setValue(inputType, undefined, { shouldDirty: true });
        }}
      />
    );
  };

  const DeleteInputButton = ({ inputType }: DeleteLogicProps) => {
    const handleDeleteAction = () => {
      if (isPrimaryAlertInput) {
        return;
      }

      setValue('secondInputField', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(inputType, undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('secondAlertType', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAdditionalLogic(false);
    };

    return (
      <TouchableOpacity
        style={styles.deleteButton}
        activeOpacity={0.75}
        onPress={() => handleDeleteAction()}
      >
        <DeleteIcon
          size={24}
          color={Colors.grayText}
        />
      </TouchableOpacity>
    );
  };

  const renderInputComponent = (type: AlertType) => {
    switch (type) {
      case AlertType.Date:
        return (
          <View style={[styles.inputRow, { marginBottom: 8 }]}>
            <View style={[styles.cropCircleInputContainer, styles.cropCircleDatePickerWrapper]}>
              <View style={styles.dateInput}>
                <DatePickerSelector
                  name="onDate"
                  setValue={setValue}
                  control={control}
                  placeholder={alertPlaceholder[type].placeholder}
                  containerStyle={datePickerContainerCropCircle}
                  calendarIconColor={tokens.colors.brand.black}
                  pickerContainerStyleIOS={datePickerModalCropCircle.pickerContainerStyleIOS}
                  modalStyleIOS={datePickerModalCropCircle.modalStyleIOS}
                  confirmButtonStyleIOS={datePickerModalCropCircle.confirmButtonStyleIOS}
                  maxYears={5}
                  minDate={moment().add(1, 'day').format('YYYY-MM-DD')}
                />
              </View>
            </View>
            {!isPrimaryAlertInput && (
              <DeleteInputButton inputType={AlertInputs.onDate} />
            )}
          </View>
        );

      case AlertType.RecurringInterval:
        return (
          <View style={styles.multipleInputContainer}>
            <View style={[styles.inputRow, { marginBottom: 8 }]}>
              <View
                style={[
                  styles.cropCircleInputContainer,
                  styles.cropCircleInputNumberContainer,
                  errors.dayInterval && styles.inputError,
                ]}
              >
                <BaseNumberInput
                  getValues={getValues}
                  setValue={setValue}
                  alertInputName={AlertInputs.dayInterval}
                  placeholder={t('alerts.number')}
                  clearButton={ClearButton}
                  inputLabelValue={t('')}
                />
              </View>
              {!isPrimaryAlertInput && (
                <DeleteInputButton inputType={AlertInputs.dayInterval} />
              )}
            </View>
            <View style={{ marginBottom: 8 }}>
              <ControlledSelect
                name={AlertInputs.dayIntervalUnit}
                control={control}
                items={dayIntervalUnitItems}
                listMode="SCROLLVIEW"
                dropDownPickerProps={getCropCircleDropDownPickerProps({
                  renderListItem: (props) => <AlertItem {...props} />,
                  onOpen: handleDropdownOpen,
                  onClose: handleDropdownClose,
                })}
                label={t('alerts.onDayIntervalUnit')}
              />
            </View>
            <View style={[styles.inputRow]}>
              <View style={[styles.cropCircleInputContainer, styles.cropCircleDatePickerWrapper]}>
                <View style={styles.dateInput}>
                  <DatePickerSelector
                    name={AlertInputs.daysStartDate}
                    setValue={setValue}
                    control={control}
                    placeholder={alertPlaceholder[AlertType.Date].placeholder}
                    containerStyle={datePickerContainerCropCircle}
                    calendarIconColor={tokens.colors.brand.black}
                    pickerContainerStyleIOS={datePickerModalCropCircle.pickerContainerStyleIOS}
                    modalStyleIOS={datePickerModalCropCircle.modalStyleIOS}
                    confirmButtonStyleIOS={datePickerModalCropCircle.confirmButtonStyleIOS}
                    maxYears={5}
                  />
                </View>
              </View>
            </View>
          </View>
        );

      case AlertType.Interval:
        return (
          <View style={styles.multipleInputContainer}>
            <View style={[styles.inputRow, { marginBottom: 8 }]}>
              <View
                style={[styles.cropCircleInputContainer, styles.cropCircleInputNumberContainer]}
              >
                <BaseNumberInput
                  getValues={getValues}
                  setValue={setValue}
                  alertInputName={AlertInputs.onInterval}
                  placeholder={alertPlaceholder[type].placeholder}
                  clearButton={ClearButton}
                  inputLabelValue={t('alerts.intervalValue')}
                />
              </View>
              {!isPrimaryAlertInput && (
                <DeleteInputButton inputType={AlertInputs.onInterval} />
              )}
            </View>
            <View style={[styles.inputRow]}>
              <View
                style={[styles.cropCircleInputContainer, styles.cropCircleInputNumberContainer]}
              >
                <BaseNumberInput
                  getValues={getValues}
                  setValue={setValue}
                  alertInputName={AlertInputs.onIntervalStart}
                  placeholder={t('alerts.startingValue')}
                  clearButton={ClearButton}
                  inputLabelValue={`${t('alerts.starting')} ${uomLabel}`}
                />
              </View>
            </View>
          </View>
        );

      case AlertType.Min:
        return (
          <View style={styles.inputRow}>
            <View style={[styles.cropCircleInputContainer, styles.cropCircleInputNumberContainer]}>
              <BaseNumberInput
                getValues={getValues}
                setValue={setValue}
                alertInputName={AlertInputs.minValue}
                placeholder={alertPlaceholder[type].placeholder}
                clearButton={ClearButton}
              />
            </View>
            {!isPrimaryAlertInput && (
              <DeleteInputButton inputType={AlertInputs.minValue} />
            )}
          </View>
        );

      case AlertType.Max:
        return (
          <View style={styles.inputRow}>
            <View style={[styles.cropCircleInputContainer, styles.cropCircleInputNumberContainer]}>
              <BaseNumberInput
                getValues={getValues}
                setValue={setValue}
                alertInputName={AlertInputs.maxValue}
                placeholder={alertPlaceholder[type].placeholder}
                clearButton={ClearButton}
              />
            </View>
            {!isPrimaryAlertInput && (
              <DeleteInputButton inputType={AlertInputs.maxValue} />
            )}
          </View>
        );

      case AlertType.RoutineCompletions:
        return (
          <View style={styles.inputRow}>
            <View style={[styles.cropCircleInputContainer, styles.cropCircleInputNumberContainer]}>
              <BaseNumberInput
                getValues={getValues}
                setValue={setValue}
                alertInputName={AlertInputs.completions}
                placeholder={alertPlaceholder[type].placeholder}
                clearButton={ClearButton}
              />
            </View>
            {!isPrimaryAlertInput && (
              <DeleteInputButton inputType={AlertInputs.completions} />
            )}
          </View>
        );

      case AlertType.DaysSkipped:
        return (
          <View style={styles.multipleInputContainer}>
            <View style={styles.inputRow}>
              <View style={[styles.cropCircleInputContainer, styles.cropCircleInputNumberContainer]}>
                <BaseNumberInput
                  getValues={getValues}
                  setValue={setValue}
                  alertInputName={AlertInputs.daysSkipped}
                  placeholder={alertPlaceholder[type].placeholder}
                  clearButton={ClearButton}
                />
              </View>
              {!isPrimaryAlertInput && (
                <DeleteInputButton inputType={AlertInputs.daysSkipped} />
              )}
            </View>
            <View style={[styles.inputRow, { marginTop: 8 }]}>
              <View style={[styles.cropCircleInputContainer, styles.cropCircleDatePickerWrapper]}>
                <View style={styles.dateInput}>
                  <DatePickerSelector
                    name={AlertInputs.daysStartDate}
                    setValue={setValue}
                    control={control}
                    placeholder={alertPlaceholder[AlertType.Date].placeholder}
                    containerStyle={datePickerContainerCropCircle}
                    calendarIconColor={tokens.colors.brand.black}
                    pickerContainerStyleIOS={datePickerModalCropCircle.pickerContainerStyleIOS}
                    modalStyleIOS={datePickerModalCropCircle.modalStyleIOS}
                    confirmButtonStyleIOS={datePickerModalCropCircle.confirmButtonStyleIOS}
                    maxYears={5}
                  />
                </View>
              </View>
            </View>
          </View>
        );

      default:
        return (
          <View>
            <Text style={styles.placeholderLabel}>Select a logic...</Text>
          </View>
        );
    }
  };

  return <View style={styles.container}>{renderInputComponent(type)}</View>;
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  inputRow: {
    flexDirection: 'row',
    width: '100%',
  },
  inputContainer: {
    borderWidth: 1,
    borderColor: Colors.grayText,
    height: 52,
    flex: 1,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  cropCircleInputContainer: {
    borderWidth: 0,
    height: 52,
    flex: 1,
    borderRadius: 4,
    backgroundColor: tokens.colors.neutral[200],
    flexDirection: 'row' as const,
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.lg,
  },
  /** Datepicker has its own 16px text/icon padding - wrapper must have no horizontal padding */
  cropCircleDatePickerWrapper: {
    paddingHorizontal: 0,
  },
  inputNumberContainer: {
    paddingHorizontal: 0,
  },
  cropCircleInputNumberContainer: {
    paddingHorizontal: 0,
  },
  multipleInputContainer: {
    flex: 1,
    width: '100%',
    overflow: 'visible' as const,
  },
  dateInput: {
    flex: 1,
    height: '100%',
  },
  inputButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
    borderWidth: 0.5,
    borderColor: 'lightblue',
    marginRight: 5,
    fontSize: 16,
    fontWeight: '400',
  },
  placeholderLabel: {
    color: Colors.grayFill,
    fontSize: 16,
    fontWeight: '400',
    flex: 1,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
  },
  inputError: {
    borderColor: Colors.red,
  },
  inputContent: {
    fontSize: 16,
    fontWeight: '400',
    color: Colors.black,
  },
  errorText: {
    color: Colors.red,
    fontSize: 12,
    marginTop: 4,
    marginBottom: 4,
  },
});
